/**
 * JS tokens mirroring packages/ui-lib/src/main.css :root.
 * Single source of truth for colors, spacing, radius, and font so API (e.g. email)
 * and other apps can use theme values without importing ui-lib.
 */

export const themeTokens = {
  // Brand + semantic (from main.css)
  brandPrimary: '#a23aff',
  brandPrimaryInvert: '#b566f9',
  brandSecondary: '#09a99c',
  info: '#5cbbff',
  success: '#40b95c',
  warning: '#ffc107',
  danger: '#ef4d61',

  // Base
  black: '#0f0f0f',
  white: '#ffffff',

  // Grays (aligned with packages/ui-lib/src/main.css :root)
  gray100: '#f8f8f8',
  gray200: '#f1f0f0',
  gray300: '#dedede',
  gray400: '#a9aeac',
  gray500: '#888d8c',
  gray600: '#5f6161',
  gray700: '#404040',
  gray800: '#323232',
  gray900: '#181818',

  // Border (maps to gray300 in light theme)
  borderColor: '#dedede',
  borderWidth: 1,

  // Surface (light theme from main.css/ui-lib ThemeColors.light)
  surfacePrimary: '#f8f8f8',
  surfaceSecondary: '#ffffff',
  surfaceTertiary: '#f8f8f8',

  // Text (light theme)
  textPrimary: '#0f0f0f',
  textPrimaryInvert: '#ffffff',
  textSecondary: '#5f6161',
  textSecondaryInvert: '#a9aeac',

  // Border radius (from main.css)
  borderRadiusXs: '0.125rem',
  borderRadiusSm: '0.25rem',
  borderRadiusMd: '0.5rem',
  borderRadiusLg: '1rem',
  borderRadiusXl: '100px',

  // Primitive colors used by button gradients
  purple600: '#6144d6',
  purple700: '#4c32b4',
  blue500: '#0095ff',

  // Button tokens (from main.css)
  buttonHeightMedium: 35,
  buttonPaddingMedium: '0 18px',
  buttonFontSizeMedium: '0.875rem',
  buttonFontWeight: 500,
  gradientButtonCta:
    'linear-gradient(95deg, #6144d6 0%, #a23aff 42%, #0095ff 100%)',

  // Gutters / spacing (from main.css --gutter-size-*)
  gutterXxxs: 1,
  gutterXxs: 4,
  gutterXs: 8,
  gutterSm: 16,
  gutterMd: 24,
  gutterLg: 32,
  gutterXl: 40,
  gutterXxl: 48,

  // Font (from main.css)
  fontFamilyPrimary: '"Inter", sans-serif',
  fontSize: '1rem',
  fontLineHeight: 1.5,

  // Aliases for consumers (e.g. email) that expect these names
  textPrimaryColor: '#0f0f0f',
  textSecondaryColor: '#5f6161',
} as const

type ThemeTokenValues = typeof themeTokens

export type ThemeTokens = {
  [K in keyof ThemeTokenValues]: ThemeTokenValues[K] extends string
    ? string
    : ThemeTokenValues[K]
}

/**
 * Dark semantic tokens mirroring packages/ui-lib/src/main.css html.dark and ThemeColors.dark.
 * Primitive colors stay fixed; semantic keys are overridden for dark rendering.
 */
export const themeTokensDark: ThemeTokens = {
  ...themeTokens,
  surfacePrimary: themeTokens.black,
  surfaceSecondary: themeTokens.black,
  surfaceTertiary: themeTokens.gray900,
  textPrimary: themeTokens.white,
  textSecondary: themeTokens.gray400,
  textPrimaryInvert: themeTokens.black,
  textSecondaryInvert: themeTokens.gray600,
  textPrimaryColor: themeTokens.white,
  textSecondaryColor: themeTokens.gray400,
  borderColor: themeTokens.gray800,
}

export type ThemeMode = 'light' | 'dark'

type VenueBrandingThemeInput = {
  theme?: ThemeMode | null
}

export function themeModeFromVenueBranding(
  branding?: VenueBrandingThemeInput | null
): ThemeMode {
  if (branding?.theme === 'light' || branding?.theme === 'dark') {
    return branding.theme
  }
  return 'dark'
}

export function logoBackgroundColorFromVenueBranding(
  branding?: VenueBrandingThemeInput | null
): string {
  return themeModeFromVenueBranding(branding) === 'light'
    ? themeTokens.white
    : themeTokens.black
}
